/* Sprint 2O — Vagtplan / Scheduling admin panel.
   Weekly grid + create/edit/publish/cancel + driver suggestions + conflict checks. */
import React from "react";
import { toast } from "sonner";
import axios from "axios";
import {
  Calendar, Plus, Send, X, Check, AlertTriangle, Loader2, ChevronLeft,
  ChevronRight, Users, Clock, MapPin, Search, Trash2, Edit2, RefreshCw,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

function adminApi() {
  const token = localStorage.getItem("ema_admin_token");
  return axios.create({
    baseURL: `${API}/api`,
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
}

const STATUS_BADGES = {
  draft: { label: "Kladde", cls: "bg-slate-100 text-slate-700" },
  published: { label: "Sendt", cls: "bg-blue-100 text-blue-800" },
  confirmed: { label: "Bekræftet", cls: "bg-green-100 text-green-800" },
  declined: { label: "Afvist", cls: "bg-red-100 text-red-800" },
  in_progress: { label: "I gang", cls: "bg-amber-100 text-amber-800" },
  completed: { label: "Afsluttet", cls: "bg-emerald-100 text-emerald-800" },
  cancelled: { label: "Annulleret", cls: "bg-slate-200 text-slate-500 line-through" },
};

const DAY_NAMES = ["Mandag", "Tirsdag", "Onsdag", "Torsdag", "Fredag", "Lørdag", "Søndag"];

function startOfWeek(d) {
  const date = new Date(d);
  const day = date.getDay() || 7; // Sunday → 7
  date.setDate(date.getDate() - day + 1);
  date.setHours(0, 0, 0, 0);
  return date;
}

function fmtDate(d) {
  return d.toISOString().slice(0, 10);
}

function addDays(d, n) {
  const r = new Date(d);
  r.setDate(r.getDate() + n);
  return r;
}

export default function VagtplanPanel() {
  const [weekStart, setWeekStart] = React.useState(startOfWeek(new Date()));
  const [shifts, setShifts] = React.useState([]);
  const [summary, setSummary] = React.useState(null);
  const [counters, setCounters] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [createOpen, setCreateOpen] = React.useState(false);
  const [editShift, setEditShift] = React.useState(null);
  const [exportOpen, setExportOpen] = React.useState(false);
  const [drivers, setDrivers] = React.useState([]);
  const [defaultDate, setDefaultDate] = React.useState(null);

  const weekEnd = addDays(weekStart, 6);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const [shiftsRes, sumRes, ctRes, drvRes] = await Promise.all([
        adminApi().get(`/admin/vagtplan/shifts?date_from=${fmtDate(weekStart)}&date_to=${fmtDate(weekEnd)}`),
        adminApi().get(`/admin/vagtplan/weekly-summary?monday=${fmtDate(weekStart)}`),
        adminApi().get(`/admin/vagtplan/control-tower`),
        adminApi().get(`/admin/drivers`),
      ]);
      setShifts(shiftsRes.data.items || []);
      setSummary(sumRes.data);
      setCounters(ctRes.data);
      setDrivers((drvRes.data || []).filter((d) => d.active !== false));
    } catch (e) {
      toast.error("Kunne ikke hente vagtplan");
    } finally {
      setLoading(false);
    }
  }, [weekStart]);

  React.useEffect(() => { load(); }, [load]);

  const onPublish = async (shiftId) => {
    try {
      await adminApi().post(`/admin/vagtplan/shifts/${shiftId}/publish`);
      toast.success("Skift publiceret til chauffør");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikke publicere");
    }
  };

  const onCancel = async (shiftId) => {
    if (!window.confirm("Annullér dette skift?")) return;
    try {
      await adminApi().request({
        method: "DELETE",
        url: `/admin/vagtplan/shifts/${shiftId}`,
        data: { reason: "Annulleret af admin" },
      });
      toast.success("Skift annulleret");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikke annullere");
    }
  };

  const onRescan = async () => {
    try {
      const r = await adminApi().post(`/admin/vagtplan/alerts/rescan`);
      toast.success(`${r.data.alerts_generated} alerts opdateret`);
    } catch (e) {
      toast.error("Alert rescan fejlede");
    }
  };

  const shiftsByDate = React.useMemo(() => {
    const map = {};
    for (let i = 0; i < 7; i++) {
      const d = fmtDate(addDays(weekStart, i));
      map[d] = [];
    }
    shifts.forEach((s) => {
      if (!map[s.date]) map[s.date] = [];
      map[s.date].push(s);
    });
    return map;
  }, [shifts, weekStart]);

  return (
    <div data-testid="vagtplan-panel">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-5">
        <div>
          <h2 className="font-display font-semibold text-2xl text-brand-dark flex items-center gap-2">
            <Calendar size={24}/> Vagtplan
          </h2>
          <p className="text-brand-text2 text-sm mt-1">
            Planlæg og publicér chauffør-vagter. Konflikter detekteres automatisk. Alle ændringer audit-logges.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setExportOpen(true)} data-testid="vagtplan-exports-btn"
            className="bg-emerald-700 hover:bg-emerald-800 text-white px-3 py-2 text-xs font-bold uppercase tracking-wider flex items-center gap-1"
          >
            📊 Eksport / Afregning
          </button>
          <button
            onClick={onRescan} data-testid="vagtplan-rescan"
            className="bg-brand-bg2 hover:bg-brand-bg2/70 text-brand-dark px-3 py-2 text-xs font-bold uppercase tracking-wider flex items-center gap-1 border border-brand"
          >
            <RefreshCw size={12}/> Kør alert-scan
          </button>
          <button
            onClick={() => { setDefaultDate(null); setCreateOpen(true); }}
            data-testid="vagtplan-create-btn"
            className="bg-brand-red hover:bg-brand-red/90 text-white px-4 py-2 text-sm font-bold tracking-wider uppercase flex items-center gap-2"
          >
            <Plus size={14}/> Opret skift
          </button>
        </div>
      </div>

      {/* KPI counters */}
      {counters && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5" data-testid="vagtplan-counters">
          <Kpi label="Skift i dag" value={counters.shifts_today_total} color="text-brand-dark"/>
          <Kpi label="Afventer bekræft" value={counters.shifts_today_unconfirmed} color="text-blue-700"/>
          <Kpi label="Afvist (i dag)" value={counters.shifts_today_declined} color="text-red-700"/>
          <Kpi label="I morgen — afvent" value={counters.shifts_tomorrow_unconfirmed} color="text-amber-700"/>
          <Kpi label="Tilg. chauffører i morgen" value={counters.drivers_available_tomorrow} color="text-emerald-700"/>
        </div>
      )}

      {/* Week navigation */}
      <div className="flex items-center justify-between mb-4">
        <button
          onClick={() => setWeekStart(addDays(weekStart, -7))} data-testid="vagtplan-prev-week"
          className="px-3 py-2 text-sm border border-brand hover:bg-brand-bg2 flex items-center gap-1"
        >
          <ChevronLeft size={14}/> Forrige uge
        </button>
        <div className="font-display font-bold text-brand-dark">
          Uge: {fmtDate(weekStart)} – {fmtDate(weekEnd)}
        </div>
        <button
          onClick={() => setWeekStart(addDays(weekStart, 7))} data-testid="vagtplan-next-week"
          className="px-3 py-2 text-sm border border-brand hover:bg-brand-bg2 flex items-center gap-1"
        >
          Næste uge <ChevronRight size={14}/>
        </button>
      </div>

      {loading && (
        <div className="text-center py-8 text-brand-text2 flex items-center justify-center gap-2">
          <Loader2 className="animate-spin" size={16}/> Indlæser vagtplan...
        </div>
      )}

      {!loading && summary && (
        <div className="mb-5 text-xs text-brand-text2 grid grid-cols-2 md:grid-cols-4 gap-3 bg-brand-bg2 p-3 border border-brand">
          <div>📊 <b>{summary.total_shifts}</b> skift i alt</div>
          <div>⏱ <b>{summary.total_hours.toFixed(1)}</b> timer planlagt</div>
          <div>👥 <b>{summary.unique_drivers}</b> unikke chauffører</div>
          <div>📅 {Object.entries(summary.by_status || {}).map(([s, c]) => `${STATUS_BADGES[s]?.label || s}: ${c}`).join(" · ") || "Ingen status"}</div>
        </div>
      )}

      {/* Week grid */}
      {!loading && (
        <div className="grid grid-cols-1 lg:grid-cols-7 gap-2" data-testid="vagtplan-grid">
          {Array.from({ length: 7 }).map((_, i) => {
            const day = addDays(weekStart, i);
            const dayStr = fmtDate(day);
            const items = shiftsByDate[dayStr] || [];
            const today = fmtDate(new Date()) === dayStr;
            return (
              <div
                key={dayStr}
                className={`bg-white border ${today ? "border-brand-red border-2" : "border-brand"} min-h-32`}
                data-testid={`vagtplan-day-${dayStr}`}
              >
                <div className={`px-2 py-1 text-xs uppercase tracking-wider font-bold flex items-center justify-between ${today ? "bg-brand-red text-white" : "bg-brand-bg2 text-brand-text2"}`}>
                  <span>{DAY_NAMES[i]} {day.getDate()}/{day.getMonth() + 1}</span>
                  <button
                    onClick={() => { setDefaultDate(dayStr); setCreateOpen(true); }}
                    data-testid={`vagtplan-add-day-${dayStr}`}
                    className={`p-0.5 ${today ? "hover:bg-white/20" : "hover:bg-brand"}`}
                    title="Tilføj skift"
                  >
                    <Plus size={12}/>
                  </button>
                </div>
                <div className="p-1 space-y-1">
                  {items.length === 0 && (
                    <div className="text-[11px] text-brand-text2 text-center py-2">Ingen skift</div>
                  )}
                  {items.map((s) => (
                    <ShiftCard
                      key={s.id} shift={s}
                      onEdit={() => setEditShift(s)}
                      onPublish={() => onPublish(s.id)}
                      onCancel={() => onCancel(s.id)}
                    />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {createOpen && (
        <CreateShiftModal
          defaultDate={defaultDate || fmtDate(new Date())}
          drivers={drivers}
          onClose={() => setCreateOpen(false)}
          onCreated={() => { setCreateOpen(false); load(); }}
        />
      )}

      {editShift && (
        <EditShiftModal
          shift={editShift}
          drivers={drivers}
          onClose={() => setEditShift(null)}
          onSaved={() => { setEditShift(null); load(); }}
        />
      )}

      {exportOpen && (
        <ExportPanel onClose={() => setExportOpen(false)}/>
      )}
    </div>
  );
}

function Kpi({ label, value, color = "text-brand-dark" }) {
  return (
    <div className="bg-white border border-brand p-3">
      <div className="text-[10px] uppercase tracking-wider text-brand-text2">{label}</div>
      <div className={`font-display font-bold text-2xl mt-1 ${color}`}>{value ?? "—"}</div>
    </div>
  );
}

function ShiftCard({ shift, onEdit, onPublish, onCancel }) {
  const badge = STATUS_BADGES[shift.status] || STATUS_BADGES.draft;
  const [linkOpen, setLinkOpen] = React.useState(false);
  const akkordMin = shift.akkord_payable_minutes || 0;
  const approvedMin = shift.approved_payable_minutes || 0;
  const hasRoutes = (shift.route_ids || []).length > 0;
  return (
    <div
      className="bg-brand-bg2 border border-brand p-1.5 text-[11px] hover:bg-white"
      data-testid={`vagtplan-shift-${shift.id}`}
    >
      <div className="flex items-center justify-between gap-1 mb-1">
        <span className={`px-1.5 py-0.5 text-[9px] font-bold uppercase ${badge.cls}`}>{badge.label}</span>
        <span className="text-brand-text2 text-[10px]">{shift.planned_hours}t</span>
      </div>
      <div className="font-semibold text-brand-dark truncate">{shift.driver_name || "—"}</div>
      <div className="text-brand-text2 flex items-center gap-1">
        <Clock size={9}/> {shift.start_time}-{shift.end_time}
      </div>
      {shift.area_preference && (
        <div className="text-brand-text2 flex items-center gap-1 text-[10px]">
          <MapPin size={9}/> {shift.area_preference}
        </div>
      )}
      <div className="flex gap-0.5 mt-1.5">
        <button onClick={onEdit} title="Rediger" data-testid={`vagtplan-edit-${shift.id}`} className="p-1 hover:bg-brand">
          <Edit2 size={10}/>
        </button>
        {shift.status === "draft" && (
          <button onClick={onPublish} title="Publicér" data-testid={`vagtplan-publish-${shift.id}`} className="p-1 hover:bg-blue-100 text-blue-700">
            <Send size={10}/>
          </button>
        )}
        <button onClick={() => setLinkOpen(true)} title="Tilknyt rute"
          data-testid={`vagtplan-link-route-${shift.id}`}
          className={`p-1 hover:bg-amber-100 ${hasRoutes ? "text-emerald-700" : "text-amber-700"}`}
        >
          🔗
        </button>
        {!["completed", "cancelled"].includes(shift.status) && (
          <button onClick={onCancel} title="Annullér" data-testid={`vagtplan-cancel-${shift.id}`} className="p-1 hover:bg-red-50 text-red-700">
            <Trash2 size={10}/>
          </button>
        )}
      </div>
      {/* Akkord info row */}
      {hasRoutes ? (
        <div className="mt-1 text-[10px] text-brand-text2 border-t border-brand pt-1" data-testid={`vagtplan-akkord-${shift.id}`}>
          🔗 {(shift.route_ids || []).length} rute(r) · Akkord: <b>{(akkordMin/60).toFixed(2)}t</b>
          {approvedMin !== akkordMin && <> · Godkendt: <b>{(approvedMin/60).toFixed(2)}t</b></>}
        </div>
      ) : (
        <div className="mt-1 text-[10px] text-amber-700 border-t border-brand pt-1">
          ⚠ Ingen rute. Akkord-tid kan ikke beregnes.
        </div>
      )}
      {linkOpen && (
        <RouteLinkModal shift={shift} onClose={() => setLinkOpen(false)} onChanged={() => { setLinkOpen(false); window.location.reload(); }}/>
      )}
    </div>
  );
}

function RouteLinkModal({ shift, onClose, onChanged }) {
  const [routeId, setRouteId] = React.useState("");
  const [override, setOverride] = React.useState(false);
  const [reason, setReason] = React.useState("");
  const [lastResult, setLastResult] = React.useState(null);
  const [adjustMin, setAdjustMin] = React.useState(0);
  const [adjustReason, setAdjustReason] = React.useState("");

  const onLink = async () => {
    try {
      const r = await adminApi().post(`/admin/vagtplan/shifts/${shift.id}/link-route`, {
        route_id: routeId.trim(),
        override_warnings: override,
        override_reason: reason || null,
      });
      setLastResult(r.data);
      if (r.data.ok) {
        toast.success("Rute tilknyttet");
        onChanged();
      } else {
        toast.error("Konflikter - sæt override + begrundelse for at fortsætte");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Tilknytning fejlede");
    }
  };

  const onUnlink = async (rid) => {
    if (!window.confirm("Fjern rute fra skift?")) return;
    try {
      await adminApi().delete(`/admin/vagtplan/shifts/${shift.id}/link-route/${rid}?reason=Admin%20fjernede`);
      toast.success("Rute frakoblet");
      onChanged();
    } catch (e) { toast.error("Frakobling fejlede"); }
  };

  const onAdjust = async () => {
    if (!adjustReason.trim()) { toast.error("Begrundelse er påkrævet"); return; }
    try {
      await adminApi().patch(`/admin/vagtplan/shifts/${shift.id}/akkord`, {
        manual_adjustment_minutes: parseInt(adjustMin) || 0,
        reason: adjustReason.trim(),
      });
      toast.success("Manuel justering gemt");
      onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Justering fejlede"); }
  };

  const onApprove = async () => {
    // Precheck for actual vs akkord deviation
    try {
      const pre = await adminApi().get(`/admin/vagtplan/shifts/${shift.id}/payroll-precheck`);
      if (pre.data?.warning) {
        const ok = window.confirm(
          `${pre.data.warning.message}\n\n${pre.data.warning.detail_da}\n\nØnsker du at fortsætte med godkendelse alligevel?`
        );
        if (!ok) return;
      }
    } catch (e) { /* precheck failure should not block — fall through */ }
    try {
      await adminApi().post(`/admin/vagtplan/shifts/${shift.id}/approve-payroll`);
      toast.success("Skift godkendt til løn");
      onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Godkendelse fejlede"); }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" data-testid="route-link-modal">
      <div className="bg-white border border-brand max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="bg-brand-dark text-white px-5 py-4 flex items-center justify-between">
          <div className="font-display font-bold">🔗 Tilknyt rute + Akkord-tid</div>
          <button onClick={onClose}><X size={18}/></button>
        </div>
        <div className="p-5 space-y-4 text-sm">
          {/* Existing links */}
          <div>
            <div className="font-bold text-xs uppercase mb-2">Tilknyttede ruter</div>
            {(shift.route_ids || []).length === 0 ? (
              <div className="text-xs text-brand-text2 bg-amber-50 border border-amber-300 p-2">
                ⚠ Ingen ruter tilknyttet. Akkord-tid kan ikke beregnes uden rute eller manuel justering.
              </div>
            ) : (
              <div className="space-y-1">
                {shift.route_ids.map((rid) => (
                  <div key={rid} className="flex items-center justify-between text-xs bg-brand-bg2 p-2 border border-brand">
                    <code className="text-[11px]">{rid}</code>
                    <button onClick={() => onUnlink(rid)} data-testid={`unlink-route-${rid}`} className="text-red-700 hover:underline">Frakobl</button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Akkord summary */}
          <div className="bg-brand-bg2 p-3 border border-brand grid grid-cols-2 gap-2 text-xs">
            <div>Algoritme: <b>{shift.route_algorithm_duration_minutes || 0} min</b></div>
            <div>Akkord: <b>{shift.akkord_payable_minutes || 0} min ({((shift.akkord_payable_minutes||0)/60).toFixed(2)}t)</b></div>
            <div>Faktisk: <b>{shift.actual_duration_minutes || "—"}</b></div>
            <div>Justering: <b>{shift.manual_adjustment_minutes || 0} min</b></div>
            <div className="col-span-2 font-bold border-t pt-1">Godkendt betalbar: <b>{shift.approved_payable_minutes || 0} min ({((shift.approved_payable_minutes||0)/60).toFixed(2)}t)</b></div>
          </div>

          {/* Add new link */}
          <div className="border border-brand p-3">
            <div className="font-bold text-xs uppercase mb-2">Tilføj rute-ID</div>
            <input
              type="text" value={routeId} onChange={(e) => setRouteId(e.target.value)}
              placeholder="Indtast rute-ID..."
              data-testid="link-route-id"
              className="w-full border border-brand p-2 text-sm mb-2"
            />
            {lastResult && lastResult.warnings && lastResult.warnings.length > 0 && (
              <div className="bg-amber-50 border border-amber-300 p-2 text-xs space-y-1 mb-2">
                {lastResult.warnings.map((w, i) => (
                  <div key={i}>⚠ <b>{w.type}</b>: {w.message}</div>
                ))}
                <label className="flex items-center gap-2 mt-2">
                  <input type="checkbox" checked={override} onChange={(e) => setOverride(e.target.checked)}/>
                  Override advarsler (begrundelse påkrævet)
                </label>
                {override && (
                  <textarea value={reason} onChange={(e) => setReason(e.target.value)}
                    rows={2} placeholder="Begrundelse..." className="w-full border border-brand p-2 mt-1"/>
                )}
              </div>
            )}
            <button onClick={onLink} disabled={!routeId.trim()}
              data-testid="link-route-submit"
              className="bg-brand-red text-white px-4 py-2 text-sm font-bold uppercase tracking-wider disabled:opacity-50">
              Tilknyt rute
            </button>
          </div>

          {/* Manual adjustment */}
          <div className="border border-brand p-3">
            <div className="font-bold text-xs uppercase mb-2">Manuel justering (kræver begrundelse)</div>
            <div className="flex gap-2 mb-2">
              <input type="number" value={adjustMin} onChange={(e) => setAdjustMin(e.target.value)}
                data-testid="adjust-minutes"
                placeholder="Min (+/-)" className="w-32 border border-brand p-2 text-sm"/>
              <input type="text" value={adjustReason} onChange={(e) => setAdjustReason(e.target.value)}
                data-testid="adjust-reason"
                placeholder="Begrundelse..." className="flex-1 border border-brand p-2 text-sm"/>
              <button onClick={onAdjust} disabled={!adjustReason.trim()}
                data-testid="adjust-submit"
                className="bg-amber-700 text-white px-4 py-2 text-xs font-bold uppercase disabled:opacity-50">
                Gem
              </button>
            </div>
          </div>

          {/* Approve for payroll */}
          {!shift.payroll_approved && (
            <button onClick={onApprove}
              data-testid="approve-payroll-btn"
              className="w-full bg-green-700 hover:bg-green-800 text-white py-3 text-sm font-bold uppercase tracking-wider">
              ✓ Godkend til lønudbetaling
            </button>
          )}
          {shift.payroll_approved && (
            <div className="bg-green-50 border border-green-300 p-3 text-xs text-green-900">
              ✓ Godkendt til løn af {shift.payroll_approved_by} ({(shift.payroll_approved_at_iso || "").slice(0, 16)})
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function CreateShiftModal({ defaultDate, drivers, onClose, onCreated }) {
  const [date, setDate] = React.useState(defaultDate);
  const [startTime, setStartTime] = React.useState("08:00");
  const [endTime, setEndTime] = React.useState("16:00");
  const [driverId, setDriverId] = React.useState("");
  const [area, setArea] = React.useState("");
  const [notes, setNotes] = React.useState("");
  const [autoPublish, setAutoPublish] = React.useState(true);
  const [suggestions, setSuggestions] = React.useState([]);
  const [loadingSugg, setLoadingSugg] = React.useState(false);
  const [conflicts, setConflicts] = React.useState([]);
  const [saving, setSaving] = React.useState(false);

  const loadSuggestions = async () => {
    if (!date || !startTime || !endTime) return;
    setLoadingSugg(true);
    try {
      const r = await adminApi().post(`/admin/vagtplan/shifts/suggest-drivers`, {
        date, start_time: startTime, end_time: endTime,
        area_preference: area || null, limit: 8,
      });
      setSuggestions(r.data.suggestions || []);
    } catch (e) { /* silent */ }
    finally { setLoadingSugg(false); }
  };

  React.useEffect(() => {
    const t = setTimeout(loadSuggestions, 400);
    return () => clearTimeout(t);
  }, [date, startTime, endTime, area]);

  const checkConflicts = async () => {
    if (!driverId || !date) { setConflicts([]); return; }
    try {
      const r = await adminApi().post(`/admin/vagtplan/shifts/check-conflicts`, {
        driver_id: driverId, date, start_time: startTime, end_time: endTime,
      });
      setConflicts(r.data.conflicts || []);
    } catch (e) { setConflicts([]); }
  };

  React.useEffect(() => {
    const t = setTimeout(checkConflicts, 400);
    return () => clearTimeout(t);
  }, [driverId, date, startTime, endTime]);

  const onSave = async () => {
    if (!driverId) { toast.error("Vælg en chauffør"); return; }
    if (!date || !startTime || !endTime) { toast.error("Udfyld dato og tider"); return; }
    setSaving(true);
    try {
      await adminApi().post(`/admin/vagtplan/shifts`, {
        driver_id: driverId, date, start_time: startTime, end_time: endTime,
        area_preference: area || null, notes: notes || null,
        auto_publish: autoPublish,
      });
      toast.success(autoPublish ? "Skift oprettet og publiceret" : "Skift gemt som kladde");
      onCreated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikke gemme");
    } finally { setSaving(false); }
  };

  const hasCritical = conflicts.some((c) => c.severity === "critical");

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" data-testid="vagtplan-create-modal">
      <div className="bg-white border border-brand max-w-3xl w-full max-h-[90vh] flex flex-col">
        <div className="bg-brand-dark text-white px-5 py-4 flex items-center justify-between">
          <div className="font-display font-bold flex items-center gap-2"><Calendar size={18}/> Opret skift</div>
          <button onClick={onClose} data-testid="vagtplan-create-close"><X size={18}/></button>
        </div>

        <div className="p-5 overflow-y-auto flex-1 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <label className="block text-xs text-brand-text2 mb-1 font-bold uppercase tracking-wider">Dato</label>
              <input type="date" value={date} onChange={(e) => setDate(e.target.value)}
                data-testid="vagtplan-create-date"
                className="w-full border border-brand p-2 text-sm focus:border-brand-red focus:outline-none"/>
            </div>
            <div>
              <label className="block text-xs text-brand-text2 mb-1 font-bold uppercase tracking-wider">Start</label>
              <input type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)}
                data-testid="vagtplan-create-start"
                className="w-full border border-brand p-2 text-sm focus:border-brand-red focus:outline-none"/>
            </div>
            <div>
              <label className="block text-xs text-brand-text2 mb-1 font-bold uppercase tracking-wider">Slut</label>
              <input type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)}
                data-testid="vagtplan-create-end"
                className="w-full border border-brand p-2 text-sm focus:border-brand-red focus:outline-none"/>
            </div>
          </div>

          <div>
            <label className="block text-xs text-brand-text2 mb-1 font-bold uppercase tracking-wider">Område (valgfri)</label>
            <input type="text" value={area} onChange={(e) => setArea(e.target.value)}
              placeholder="fx Sjælland, København"
              data-testid="vagtplan-create-area"
              className="w-full border border-brand p-2 text-sm focus:border-brand-red focus:outline-none"/>
          </div>

          <div>
            <label className="block text-xs text-brand-text2 mb-1 font-bold uppercase tracking-wider">
              Foreslået chauffør {loadingSugg && <Loader2 className="animate-spin inline" size={10}/>}
            </label>
            {suggestions.length > 0 && (
              <div className="border border-brand max-h-48 overflow-y-auto mb-2" data-testid="vagtplan-suggestions">
                {suggestions.slice(0, 6).map((s) => (
                  <button
                    key={s.driver_id}
                    type="button"
                    onClick={() => setDriverId(s.driver_id)}
                    data-testid={`vagtplan-suggest-${s.driver_id}`}
                    className={`w-full text-left px-3 py-2 text-xs border-b border-brand last:border-b-0 hover:bg-brand-bg2 flex items-center justify-between ${driverId === s.driver_id ? "bg-brand-bg2 border-l-4 border-l-brand-red" : ""}`}
                  >
                    <div>
                      <div className="font-semibold text-brand-dark">{s.driver_name}</div>
                      <div className="text-brand-text2 text-[10px]">{s.reasons.slice(0, 3).join(" · ") || "Ingen begrundelse"}</div>
                    </div>
                    <span className={`px-2 py-0.5 font-bold ${s.score >= 50 ? "text-green-700" : s.score >= 0 ? "text-amber-700" : "text-red-700"}`}>
                      {s.score}
                    </span>
                  </button>
                ))}
              </div>
            )}
            <select
              value={driverId} onChange={(e) => setDriverId(e.target.value)}
              data-testid="vagtplan-create-driver"
              className="w-full border border-brand p-2 text-sm"
            >
              <option value="">-- Vælg chauffør --</option>
              {drivers.map((d) => (
                <option key={d.id} value={d.id}>{d.name || d.email}</option>
              ))}
            </select>
          </div>

          {conflicts.length > 0 && (
            <div className={`border-2 p-3 ${hasCritical ? "border-red-300 bg-red-50" : "border-amber-300 bg-amber-50"}`} data-testid="vagtplan-conflicts">
              <div className="font-bold flex items-center gap-2 mb-2">
                <AlertTriangle size={16} className={hasCritical ? "text-red-700" : "text-amber-700"}/>
                {hasCritical ? "Kritiske konflikter" : "Advarsler"}
              </div>
              {conflicts.map((c, i) => (
                <div key={i} className="text-xs text-brand-dark mb-1">
                  • [{c.severity === "critical" ? "❌" : "⚠"}] {c.message}
                </div>
              ))}
            </div>
          )}

          <div>
            <label className="block text-xs text-brand-text2 mb-1 font-bold uppercase tracking-wider">Noter (valgfri)</label>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)}
              rows={2} data-testid="vagtplan-create-notes"
              className="w-full border border-brand p-2 text-sm focus:border-brand-red focus:outline-none"/>
          </div>

          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={autoPublish} onChange={(e) => setAutoPublish(e.target.checked)}
              data-testid="vagtplan-create-publish"/>
            Publicér straks (notificér chauffør)
          </label>
        </div>

        <div className="border-t border-brand p-4 flex justify-end gap-3 bg-brand-bg2">
          <button onClick={onClose} className="px-4 py-2 text-sm text-brand-text2">Annullér</button>
          <button onClick={onSave} disabled={saving || hasCritical}
            data-testid="vagtplan-create-save"
            className="bg-brand-red hover:bg-brand-red/90 text-white px-5 py-2 text-sm font-bold tracking-wider uppercase disabled:opacity-50 flex items-center gap-2">
            {saving ? <Loader2 size={14} className="animate-spin"/> : <Check size={14}/>}
            {hasCritical ? "Kritiske konflikter - kan ikke gemmes" : "Gem skift"}
          </button>
        </div>
      </div>
    </div>
  );
}

function EditShiftModal({ shift, drivers, onClose, onSaved }) {
  const [date, setDate] = React.useState(shift.date);
  const [startTime, setStartTime] = React.useState(shift.start_time);
  const [endTime, setEndTime] = React.useState(shift.end_time);
  const [driverId, setDriverId] = React.useState(shift.driver_id);
  const [notes, setNotes] = React.useState(shift.notes || "");
  const [saving, setSaving] = React.useState(false);

  const onSave = async () => {
    setSaving(true);
    try {
      await adminApi().patch(`/admin/vagtplan/shifts/${shift.id}`, {
        date, start_time: startTime, end_time: endTime,
        driver_id: driverId, notes: notes || null,
      });
      toast.success("Skift opdateret");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikke gemme");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" data-testid="vagtplan-edit-modal">
      <div className="bg-white border border-brand max-w-2xl w-full">
        <div className="bg-brand-dark text-white px-5 py-4 flex items-center justify-between">
          <div className="font-display font-bold">Rediger skift</div>
          <button onClick={onClose}><X size={18}/></button>
        </div>
        <div className="p-5 space-y-3">
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block text-xs mb-1 font-bold uppercase">Dato</label>
              <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="w-full border border-brand p-2 text-sm"/>
            </div>
            <div>
              <label className="block text-xs mb-1 font-bold uppercase">Start</label>
              <input type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} className="w-full border border-brand p-2 text-sm"/>
            </div>
            <div>
              <label className="block text-xs mb-1 font-bold uppercase">Slut</label>
              <input type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)} className="w-full border border-brand p-2 text-sm"/>
            </div>
          </div>
          <div>
            <label className="block text-xs mb-1 font-bold uppercase">Chauffør</label>
            <select value={driverId} onChange={(e) => setDriverId(e.target.value)} className="w-full border border-brand p-2 text-sm">
              {drivers.map((d) => <option key={d.id} value={d.id}>{d.name || d.email}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs mb-1 font-bold uppercase">Noter</label>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className="w-full border border-brand p-2 text-sm"/>
          </div>
        </div>
        <div className="border-t border-brand p-4 flex justify-end gap-3 bg-brand-bg2">
          <button onClick={onClose} className="px-4 py-2 text-sm">Annullér</button>
          <button onClick={onSave} disabled={saving}
            className="bg-brand-red hover:bg-brand-red/90 text-white px-5 py-2 text-sm font-bold tracking-wider uppercase disabled:opacity-50">
            {saving ? "Gemmer..." : "Gem ændringer"}
          </button>
        </div>
      </div>
    </div>
  );
}

function ExportPanel({ onClose }) {
  const today = new Date();
  const firstOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  const lastOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
  const fmt = (d) => d.toISOString().slice(0, 10);

  const [tab, setTab] = React.useState("salary");
  const [dateFrom, setDateFrom] = React.useState(fmt(firstOfMonth));
  const [dateTo, setDateTo] = React.useState(fmt(lastOfMonth));
  const [approvedOnly, setApprovedOnly] = React.useState(true);
  const [preview, setPreview] = React.useState(null);
  const [loading, setLoading] = React.useState(false);

  // Subcontractor settlement state
  const [subs, setSubs] = React.useState([]);
  const [scId, setScId] = React.useState("");
  const [period, setPeriod] = React.useState(fmt(today).slice(0, 7));
  const [rate, setRate] = React.useState(250);
  const [vat, setVat] = React.useState(25);
  const [scPreview, setScPreview] = React.useState(null);
  const [emails, setEmails] = React.useState([]);

  React.useEffect(() => {
    adminApi().get("/admin/subcontractors").then((r) => {
      setSubs((r.data?.items || r.data || []).filter((s) => s.active !== false));
    }).catch(() => {});
    adminApi().get("/admin/vagtplan/settlement/emails").then((r) => setEmails(r.data?.items || [])).catch(() => {});
  }, []);

  const loadPreview = async () => {
    setLoading(true);
    try {
      const r = await adminApi().get(`/admin/vagtplan/exports/preview?date_from=${dateFrom}&date_to=${dateTo}&approved_only=${approvedOnly}`);
      setPreview(r.data);
    } catch (e) { toast.error("Kunne ikke hente preview"); }
    finally { setLoading(false); }
  };

  React.useEffect(() => { if (tab !== "settlement") loadPreview(); }, [tab, dateFrom, dateTo, approvedOnly]);

  const download = async (url, filename) => {
    try {
      const r = await adminApi().get(url, { responseType: "blob" });
      const blob = new Blob([r.data]);
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = filename;
      link.click();
      toast.success(`Downloaded: ${filename}`);
    } catch (e) { toast.error("Download fejlede"); }
  };

  const loadScPreview = async () => {
    if (!scId || !period) return;
    try {
      const r = await adminApi().get(`/admin/vagtplan/settlement/${scId}/${period}/preview?rate_per_hour=${rate}&vat_pct=${vat}`);
      setScPreview(r.data);
    } catch (e) { toast.error("Kunne ikke hente afregning"); }
  };

  const sendSettlement = async (force = false) => {
    try {
      const r = await adminApi().post(`/admin/vagtplan/settlement/${scId}/${period}/send?rate_per_hour=${rate}&vat_pct=${vat}`, { force });
      const status = r.data?.status || (r.data?.skipped ? "skipped" : "ok");
      toast.success(`Status: ${status}`);
      const er = await adminApi().get("/admin/vagtplan/settlement/emails");
      setEmails(er.data?.items || []);
    } catch (e) { toast.error("Afsendelse fejlede"); }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" data-testid="export-panel">
      <div className="bg-white border border-brand max-w-5xl w-full max-h-[92vh] overflow-y-auto">
        <div className="bg-brand-dark text-white px-5 py-4 flex items-center justify-between">
          <div className="font-display font-bold">📊 Eksport / Afregning</div>
          <button onClick={onClose}><X size={18}/></button>
        </div>
        <div className="border-b border-brand flex">
          <button onClick={() => setTab("salary")} data-testid="export-tab-salary"
            className={`flex-1 py-3 text-sm font-bold uppercase tracking-wider ${tab === "salary" ? "bg-brand-bg2 border-b-2 border-brand-red text-brand-red" : "text-brand-text2"}`}>
            EMA Løn-eksport
          </button>
          <button onClick={() => setTab("planday")} data-testid="export-tab-planday"
            className={`flex-1 py-3 text-sm font-bold uppercase tracking-wider ${tab === "planday" ? "bg-brand-bg2 border-b-2 border-brand-red text-brand-red" : "text-brand-text2"}`}>
            Planday-eksport
          </button>
          <button onClick={() => setTab("settlement")} data-testid="export-tab-settlement"
            className={`flex-1 py-3 text-sm font-bold uppercase tracking-wider ${tab === "settlement" ? "bg-brand-bg2 border-b-2 border-brand-red text-brand-red" : "text-brand-text2"}`}>
            Vognmandsafregning
          </button>
          <button onClick={() => setTab("sms")} data-testid="export-tab-sms"
            className={`flex-1 py-3 text-sm font-bold uppercase tracking-wider ${tab === "sms" ? "bg-brand-bg2 border-b-2 border-brand-red text-brand-red" : "text-brand-text2"}`}>
            SMS-påmindelser
          </button>
        </div>

        <div className="p-5">
          {/* Salary / Planday tabs */}
          {(tab === "salary" || tab === "planday") && (
            <>
              <div className="flex flex-wrap gap-3 mb-4 items-end">
                <div>
                  <label className="block text-xs uppercase tracking-wider font-bold mb-1">Fra</label>
                  <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
                    data-testid="export-date-from" className="border border-brand p-2 text-sm"/>
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-wider font-bold mb-1">Til</label>
                  <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
                    data-testid="export-date-to" className="border border-brand p-2 text-sm"/>
                </div>
                {tab === "salary" && (
                  <label className="flex items-center gap-2 text-xs">
                    <input type="checkbox" checked={approvedOnly} onChange={(e) => setApprovedOnly(e.target.checked)}/>
                    Kun godkendte
                  </label>
                )}
                {tab === "salary" && (
                  <>
                    <button onClick={() => download(`/admin/vagtplan/exports/salary.csv?date_from=${dateFrom}&date_to=${dateTo}&approved_only=${approvedOnly}`, `ema_salary_${dateFrom}_${dateTo}.csv`)}
                      data-testid="download-salary-csv"
                      className="bg-emerald-700 hover:bg-emerald-800 text-white px-4 py-2 text-xs font-bold uppercase">
                      ⬇ Salary CSV
                    </button>
                    <button onClick={() => download(`/admin/vagtplan/exports/salary.xlsx?date_from=${dateFrom}&date_to=${dateTo}&approved_only=${approvedOnly}`, `ema_salary_${dateFrom}_${dateTo}.xlsx`)}
                      data-testid="download-salary-xlsx"
                      className="bg-emerald-700 hover:bg-emerald-800 text-white px-4 py-2 text-xs font-bold uppercase">
                      ⬇ Salary XLSX
                    </button>
                  </>
                )}
                {tab === "planday" && (
                  <button onClick={() => download(`/admin/vagtplan/exports/planday.csv?date_from=${dateFrom}&date_to=${dateTo}`, `ema_planday_${dateFrom}_${dateTo}.csv`)}
                    data-testid="download-planday-csv"
                    className="bg-emerald-700 hover:bg-emerald-800 text-white px-4 py-2 text-xs font-bold uppercase">
                    ⬇ Planday CSV
                  </button>
                )}
              </div>
              {loading && <div className="text-xs text-brand-text2 flex items-center gap-2"><Loader2 className="animate-spin" size={12}/>Indlæser...</div>}
              {preview && (
                <div data-testid="export-preview">
                  <p className="text-xs text-brand-text2 mb-2">
                    {tab === "salary"
                      ? `${preview.salary.count} løn-rækker (kun EMA interne, ${approvedOnly ? "kun godkendte" : "alle"})`
                      : `${preview.planday.count} Planday-rækker (kun EMA interne)`}
                  </p>
                  <div className="overflow-x-auto border border-brand max-h-96">
                    <table className="w-full text-xs">
                      <thead className="bg-brand-bg2 sticky top-0">
                        <tr>
                          {((tab === "salary" ? preview.salary : preview.planday).rows[0] && Object.keys((tab === "salary" ? preview.salary : preview.planday).rows[0])) ?
                            Object.keys((tab === "salary" ? preview.salary : preview.planday).rows[0]).map((h) => (
                              <th key={h} className="px-2 py-1 text-left text-[10px] uppercase border-b border-brand whitespace-nowrap">{h}</th>
                            )) :
                            <th className="px-2 py-1">Ingen data</th>
                          }
                        </tr>
                      </thead>
                      <tbody>
                        {(tab === "salary" ? preview.salary : preview.planday).rows.slice(0, 50).map((r, i) => (
                          <tr key={i} className="hover:bg-brand-bg2/50">
                            {Object.values(r).map((v, j) => (
                              <td key={j} className="px-2 py-1 border-b border-brand whitespace-nowrap text-[11px]">{String(v ?? "")}</td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          )}

          {/* Settlement tab */}
          {tab === "settlement" && (
            <div className="space-y-4">
              <div className="bg-brand-bg2 border border-brand p-3 text-xs">
                ℹ Månedlig vognmandsafregning kører automatisk d. 2. i hver måned kl. 09:00 KBH og sender forrige måneds afregning til alle vognmænd.
                Hvis SMTP mangler logges afsendelsen som mocked. Brug knappen herunder til manuel afsendelse / preview.
              </div>
              <div className="flex flex-wrap gap-3 items-end">
                <div>
                  <label className="block text-xs uppercase tracking-wider font-bold mb-1">Vognmand</label>
                  <select value={scId} onChange={(e) => { setScId(e.target.value); setScPreview(null); }}
                    data-testid="settlement-sc-select" className="border border-brand p-2 text-sm min-w-64">
                    <option value="">-- Vælg vognmand --</option>
                    {subs.map((s) => <option key={s.id} value={s.id}>{s.name || s.company_name || s.id}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-wider font-bold mb-1">Periode (YYYY-MM)</label>
                  <input type="month" value={period} onChange={(e) => setPeriod(e.target.value)}
                    data-testid="settlement-period" className="border border-brand p-2 text-sm"/>
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-wider font-bold mb-1">Time-sats (DKK)</label>
                  <input type="number" value={rate} onChange={(e) => setRate(e.target.value)} className="border border-brand p-2 text-sm w-24"/>
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-wider font-bold mb-1">Moms %</label>
                  <input type="number" value={vat} onChange={(e) => setVat(e.target.value)} className="border border-brand p-2 text-sm w-20"/>
                </div>
                <button onClick={loadScPreview} disabled={!scId}
                  data-testid="settlement-preview-btn"
                  className="bg-brand-bg2 border border-brand px-4 py-2 text-xs font-bold uppercase">
                  Preview
                </button>
                <button onClick={() => download(`/admin/vagtplan/settlement/${scId}/${period}/workbook.xlsx?rate_per_hour=${rate}&vat_pct=${vat}`, `Vognmandafregning_${period}.xlsx`)}
                  disabled={!scId}
                  data-testid="settlement-download-btn"
                  className="bg-emerald-700 text-white px-4 py-2 text-xs font-bold uppercase disabled:opacity-50">
                  ⬇ Download XLSX
                </button>
                <button onClick={() => sendSettlement(false)} disabled={!scId}
                  data-testid="settlement-send-btn"
                  className="bg-brand-red text-white px-4 py-2 text-xs font-bold uppercase disabled:opacity-50">
                  ✉ Send til vognmand
                </button>
              </div>
              {scPreview && (
                <div className="bg-white border-2 border-brand p-4 text-sm" data-testid="settlement-preview-result">
                  <div className="font-display font-bold mb-2">{scPreview.subcontractor_name} (CVR {scPreview.subcontractor_cvr || "—"})</div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                    <div>Email: <b>{scPreview.subcontractor_email}</b></div>
                    <div>Periode: <b>{scPreview.period}</b></div>
                    <div>Antal ruter: <b>{scPreview.route_count}</b></div>
                    <div>Total ex moms: <b>{scPreview.total_ex_vat?.toFixed(2)} DKK</b></div>
                    <div>Moms: <b>{scPreview.vat_amount?.toFixed(2)} DKK</b></div>
                    <div className="font-bold text-brand-red">Total inkl. moms: <b>{scPreview.total_inc_vat?.toFixed(2)} DKK</b></div>
                  </div>
                </div>
              )}
              <div>
                <div className="font-bold text-xs uppercase tracking-wider mb-2">Seneste afsendelser</div>
                <div className="overflow-x-auto border border-brand max-h-64">
                  <table className="w-full text-xs" data-testid="settlement-emails-log">
                    <thead className="bg-brand-bg2 sticky top-0">
                      <tr>
                        <th className="px-2 py-1 text-left text-[10px] uppercase">Periode</th>
                        <th className="px-2 py-1 text-left text-[10px] uppercase">Vognmand</th>
                        <th className="px-2 py-1 text-left text-[10px] uppercase">Status</th>
                        <th className="px-2 py-1 text-left text-[10px] uppercase">Total ex moms</th>
                        <th className="px-2 py-1 text-left text-[10px] uppercase">Tid</th>
                      </tr>
                    </thead>
                    <tbody>
                      {emails.length === 0 && <tr><td colSpan={5} className="px-2 py-3 text-center text-brand-text2">Ingen afsendelser endnu</td></tr>}
                      {emails.map((e) => (
                        <tr key={e.id} className="hover:bg-brand-bg2/50">
                          <td className="px-2 py-1 border-b border-brand">{e.period}</td>
                          <td className="px-2 py-1 border-b border-brand">{e.subcontractor_name}</td>
                          <td className="px-2 py-1 border-b border-brand">
                            <span className={`px-1.5 py-0.5 text-[10px] font-bold ${e.status === "sent" ? "bg-green-100 text-green-800" : e.status === "mocked" ? "bg-amber-100 text-amber-800" : "bg-red-100 text-red-800"}`}>
                              {e.status}
                            </span>
                          </td>
                          <td className="px-2 py-1 border-b border-brand">{e.total_ex_vat?.toFixed(2)}</td>
                          <td className="px-2 py-1 border-b border-brand text-[10px]">{(e.sent_at_iso || e.created_at_iso || "").slice(0, 16)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* SMS-påmindelser tab */}
          {tab === "sms" && <SmsLogTab/>}
        </div>
      </div>
    </div>
  );
}

function SmsLogTab() {
  const [items, setItems] = React.useState([]);
  const [summary, setSummary] = React.useState({});
  const [running, setRunning] = React.useState(false);
  const [filter, setFilter] = React.useState("");

  const load = React.useCallback(async () => {
    try {
      const r = await adminApi().get("/admin/vagtplan/sms/log?limit=200");
      setItems(r.data.items || []);
      setSummary(r.data.summary || {});
    } catch (e) { toast.error("Kunne ikke hente SMS log"); }
  }, []);

  React.useEffect(() => { load(); }, [load]);

  const runJob = async () => {
    setRunning(true);
    try {
      const r = await adminApi().post("/admin/vagtplan/sms/run-daily-job");
      toast.success(`Job kørt: ${r.data.sent} sendt · ${r.data.mocked} mocked · ${r.data.duplicate_skipped} duplikater`);
      load();
    } catch (e) { toast.error("Job fejlede"); }
    finally { setRunning(false); }
  };

  const resend = async (it) => {
    if (!window.confirm(`Send påmindelse igen til ${it.driver_name}?`)) return;
    try {
      await adminApi().post("/admin/vagtplan/sms/resend", {
        shift_id: it.shift_id, driver_id: it.driver_id,
      });
      toast.success("Påmindelse gensendt");
      load();
    } catch (e) { toast.error("Gensend fejlede"); }
  };

  const filtered = filter
    ? items.filter((i) => (i.driver_name || "").toLowerCase().includes(filter.toLowerCase()) ||
                          (i.phone_masked || "").includes(filter) ||
                          (i.status || "").includes(filter))
    : items;

  return (
    <div className="space-y-4" data-testid="sms-log-tab">
      <div className="bg-brand-bg2 border border-brand p-3 text-xs">
        ℹ Daglig SMS-påmindelse kører kl. <b>14:00 Europe/Copenhagen</b> til alle chauffører med vagt samme dag.
        Hvis Twilio ikke er konfigureret (eller credentials er ugyldige) logges som <b>mocked</b> uden at sende.
        SMS indeholder aldrig kundedata — kun chauffør+skift+pickup-info.
        {summary.twilio_configured === false && (
          <div className="text-amber-700 font-bold mt-1">⚠ Twilio er IKKE konfigureret (eller credentials ugyldige) — alle SMS logges som mocked.</div>
        )}
      </div>

      <div className="flex flex-wrap gap-3 items-center">
        <button onClick={runJob} disabled={running}
          data-testid="sms-run-job-btn"
          className="bg-brand-red text-white px-4 py-2 text-xs font-bold uppercase tracking-wider disabled:opacity-50">
          {running ? "Kører..." : "▶ Kør 14:00 job nu"}
        </button>
        <input type="text" value={filter} onChange={(e) => setFilter(e.target.value)}
          placeholder="Filter: navn / status / telefon"
          data-testid="sms-log-filter"
          className="border border-brand p-2 text-sm flex-1 max-w-md"/>
        <div className="text-xs text-brand-text2 ml-auto flex gap-3">
          <span>📤 Sendt: <b>{summary.sent || 0}</b></span>
          <span>📋 Mocked: <b>{summary.mocked || 0}</b></span>
          <span>❌ Fejlet: <b>{summary.failed || 0}</b></span>
          <span>⏭ Dup: <b>{summary.duplicate_skipped || 0}</b></span>
        </div>
      </div>

      <div className="overflow-x-auto border border-brand max-h-96">
        <table className="w-full text-xs" data-testid="sms-log-table">
          <thead className="bg-brand-bg2 sticky top-0">
            <tr>
              <th className="px-2 py-1 text-left text-[10px] uppercase">Chauffør</th>
              <th className="px-2 py-1 text-left text-[10px] uppercase">Telefon</th>
              <th className="px-2 py-1 text-left text-[10px] uppercase">Dato</th>
              <th className="px-2 py-1 text-left text-[10px] uppercase">Tid</th>
              <th className="px-2 py-1 text-left text-[10px] uppercase">Rute</th>
              <th className="px-2 py-1 text-left text-[10px] uppercase">Sprog</th>
              <th className="px-2 py-1 text-left text-[10px] uppercase">Status</th>
              <th className="px-2 py-1 text-left text-[10px] uppercase">Tid logget</th>
              <th className="px-2 py-1 text-left text-[10px] uppercase">Reason / Fejl</th>
              <th className="px-2 py-1"></th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && <tr><td colSpan={10} className="px-2 py-4 text-center text-brand-text2">Ingen log-entries</td></tr>}
            {filtered.slice(0, 100).map((it) => (
              <tr key={it.id} data-testid={`sms-log-row-${it.id}`} className="hover:bg-brand-bg2/50">
                <td className="px-2 py-1 border-b border-brand">{it.driver_name}</td>
                <td className="px-2 py-1 border-b border-brand"><code className="text-[10px]">{it.phone_masked}</code></td>
                <td className="px-2 py-1 border-b border-brand">{it.shift_date}</td>
                <td className="px-2 py-1 border-b border-brand">{it.shift_start}-{it.shift_end}</td>
                <td className="px-2 py-1 border-b border-brand text-[10px]">{(it.route_ids || []).slice(0, 2).join(", ") || "—"}</td>
                <td className="px-2 py-1 border-b border-brand"><span className="px-1.5 py-0.5 bg-brand-bg2 text-[10px]">{it.language}</span></td>
                <td className="px-2 py-1 border-b border-brand">
                  <span className={`px-1.5 py-0.5 text-[10px] font-bold ${
                    it.status === "sent" ? "bg-green-100 text-green-800" :
                    it.status === "mocked" ? "bg-amber-100 text-amber-800" :
                    it.status === "duplicate_skipped" ? "bg-slate-100 text-slate-600" :
                    "bg-red-100 text-red-800"
                  }`}>{it.status}</span>
                </td>
                <td className="px-2 py-1 border-b border-brand text-[10px]">{(it.sent_at_iso || it.created_at_iso || "").slice(0, 16)}</td>
                <td className="px-2 py-1 border-b border-brand text-[10px] max-w-xs truncate" title={it.error || it.reason}>{it.error || it.reason || ""}</td>
                <td className="px-2 py-1 border-b border-brand">
                  <button onClick={() => resend(it)} data-testid={`sms-resend-${it.id}`}
                    className="text-blue-700 hover:underline text-[10px]">Gensend</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

